/**
 * Sign-in screens (new; India login.jsx flow only — email + password, plus a magic link). No token in localStorage:
 * the API sets the HttpOnly session cookie. `next` is restricted to same-origin paths (scenario S1.2).
 */
import { useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { BRAND } from '@shared/contracts.mjs';
import { useData } from '@app/core/provider';
import { safeNext, stepUrl } from '@app/core/auth';
import { errorMessage } from './feedback';

/** `title` omitted: the content brings its own heading (the second sign-in step). `scroll`: content may outgrow a phone screen. */
export function AuthCard({ title, children, foot, scroll }: { title?: string; children: React.ReactNode; foot?: React.ReactNode; scroll?: boolean }) {
  return (
    <div className={['stg', 'theme-dark', 'is-fs', 'stg--app', 'app-auth', scroll && 'app-auth--scroll'].filter(Boolean).join(' ')}>
      <div className="app-auth__card">
        <img className="app-auth__brand" src="/brand/logo-horizontal-dark.svg" width={175} height={30} alt="Ride N Repair OS" />
        {title && <h1>{title}</h1>}
        {children}
        {foot && <div className="app-auth__foot">{foot}</div>}
      </div>
      <p className="app-auth__legal stg-muted">{BRAND.company}</p>
    </div>
  );
}

export function Login() {
  const { data } = useData();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  // `reason=expired`: sent back here from the second sign-in step because the sign-in it belonged to had lapsed.
  const [msg, setMsg] = useState<{ tone: 'err' | 'ok'; text: string } | null>(
    sp.get('reason') === 'expired' ? { tone: 'err', text: 'Your sign-in timed out before the second step was finished. Sign in again.' } : null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const step = await data.session.login(email.trim(), password);
      qc.clear();
      // The password was right, but the session may still owe the second sign-in step: its screen comes first.
      nav(step === 'none' ? safeNext(sp.get('next')) : stepUrl(safeNext(sp.get('next'))), { replace: true });
    } catch (err) {
      setMsg({ tone: 'err', text: errorMessage(err) === 'invalid credentials' ? 'That email and password don’t match.' : errorMessage(err) });
    } finally { setBusy(false); }
  };
  const magic = async () => {
    if (!email.includes('@')) { setMsg({ tone: 'err', text: 'Enter your email first.' }); return; }
    setBusy(true);
    try { await data.session.requestMagicLink(email.trim()); setMsg({ tone: 'ok', text: 'Check your inbox for a sign-in link.' }); }
    catch (err) { setMsg({ tone: 'err', text: errorMessage(err) }); } finally { setBusy(false); }
  };
  return (
    <AuthCard title="Sign in" foot={<><a href="/reset">Forgot password?</a>{data.mode === 'fixture' && <span className="stg-muted"> · Sample tenant: any email + 4-char password</span>}</>}>
      <form onSubmit={submit} className="app-form">
        <label className="app-field"><span>Email</span><input className="app-input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus /></label>
        <label className="app-field"><span>Password</span><input className="app-input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></label>
        {msg && <p className={msg.tone === 'err' ? 'app-err' : 'app-ok'} role="alert">{msg.text}</p>}
        <button className="btn btn--primary btn--block" type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
        <button className="btn btn--ghost btn--block btn--sm" type="button" onClick={magic} disabled={busy}>Email me a sign-in link</button>
      </form>
    </AuthCard>
  );
}

export function ResetPassword() {
  const { data } = useData();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <AuthCard title="Reset your password" foot={<a href="/login">Back to sign in</a>}>
      {sent ? <p className="app-ok">If that email has an account, a reset link is on its way.</p> : (
        <form className="app-form" onSubmit={async (e) => { e.preventDefault(); try { await data.session.resetPassword(email.trim()); setSent(true); } catch (x) { setErr(errorMessage(x)); } }}>
          <label className="app-field"><span>Email</span><input className="app-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus /></label>
          {err && <p className="app-err" role="alert">{err}</p>}
          <button className="btn btn--ink btn--block" type="submit">Send reset link</button>
        </form>
      )}
    </AuthCard>
  );
}

export function AcceptInvite() {
  const { data } = useData();
  const { token = '' } = useParams();
  const nav = useNavigate();
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState<string | null>(null);
  return (
    <AuthCard title="Join your team">
      <form className="app-form" onSubmit={async (e) => {
        e.preventDefault();
        if (pw.length < 10) { setErr('Use at least 10 characters.'); return; }
        try { await data.session.acceptInvite(token, pw, { first_name: first.trim(), last_name: last.trim() }); nav('/today', { replace: true }); } catch (x) { setErr(errorMessage(x)); }
      }}>
        <div className="app-row2">
          <label className="app-field"><span>First name</span><input className="app-input" required value={first} onChange={(e) => setFirst(e.target.value)} autoFocus /></label>
          <label className="app-field"><span>Last name</span><input className="app-input" required value={last} onChange={(e) => setLast(e.target.value)} /></label>
        </div>
        <label className="app-field"><span>Choose a password</span><input className="app-input" type="password" autoComplete="new-password" required value={pw} onChange={(e) => setPw(e.target.value)} /></label>
        {err && <p className="app-err" role="alert">{err}</p>}
        <button className="btn btn--primary btn--block" type="submit">Create my account</button>
      </form>
    </AuthCard>
  );
}
